import { ReactElement } from 'react';
import { Box, Typography } from '@mui/material';
import { SanitizedHtml, useAsyncValue } from 'framework/utils';
import { useApplicationSettings } from 'framework/uiTheme';

export type ErrorMessage = string | string[];

export interface BaseInputErrorsProps {
  errorMessages?: Nullable<ErrorMessage[]>;
  errorMessageMapper?: (error: ErrorMessage) => string | string[];
}

const formatError = (error: string) => {
  return error.replace(/(\d{4})-(\d{2})-(\d{2})/g, '$1&#8209;$2&#8209;$3');
};

const ErrorMessageComponent = ({
  error,
  mapTags,
  mapAsyncTags,
}: {
  error: string;
  mapTags: any;
  mapAsyncTags: any;
}) => {
  const syncData = mapTags(error);
  const mappedTags = useAsyncValue(async () => mapAsyncTags(syncData));

  return <SanitizedHtml wrapperTag="span" html={formatError(mappedTags || syncData)} />;
};

const BaseInputErrors = ({ errorMessages = [], errorMessageMapper }: BaseInputErrorsProps) => {
  const { useDefaultTextTransformer } = useApplicationSettings();
  const { mapAsyncTags = () => {}, mapTags = () => {} } = useDefaultTextTransformer?.() ?? {};

  if (!errorMessages || !errorMessages.length) {
    return null;
  }

  let displayedErrors: (ReactElement | ErrorMessage)[] = errorMessages;

  if (errorMessageMapper) {
    displayedErrors = errorMessages.map(errorMessageMapper);
  }

  displayedErrors = displayedErrors.map(error =>
    typeof error === 'string' ? (
      <ErrorMessageComponent error={error} mapTags={mapTags} mapAsyncTags={mapAsyncTags} key={error} />
    ) : (
      error
    ),
  );

  return (
    <>
      {displayedErrors.map((error, index) => (
        <Box className="form-error" key={`${typeof error === 'string' ? error : index}`}>
          {typeof error === 'string' ? (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          ) : (
            error
          )}
        </Box>
      ))}
    </>
  );
};

export default BaseInputErrors;
